import mixpanel from "mixpanel-browser"
import { useCallback, useEffect, useMemo, useState } from "react"
import { read, write } from "xlsx"
import {
    AcceptFileType,
    AcceptFileTypeExtensions,
    convertExtensionToMimeType,
} from "../../projects/components/CreateProjectForm/FileField"
import { Stage } from "../components/Progress"

import { preloadAIAssets } from "../services/preloadAIAssets"
import { processItemsInWorkerPool } from "../services/processItemsInWorkerPool"

const sessionKey = "pii-scanner"
const acceptedTermsKey = "acceptedTerms"

export const accepts = [
    AcceptFileType.CSV,
    AcceptFileType.TSV,
    AcceptFileType.TXT,
    AcceptFileType.XLS,
    AcceptFileType.XLSX,
]
const acceptedExtensions = accepts.map((type) => AcceptFileTypeExtensions[type])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function processCell(cell: any, action: (val: string) => string) {
    if (cell.t === "s" && cell.v.length > 0) {
        cell.v = action(cell.v)
    } else if (cell.t === "n" && (cell?.w ?? "").length > 0) {
        const redactedContent = action(cell.w)

        if (redactedContent !== cell.w) {
            cell.t = "s"
            cell.v = action(cell.w)
            delete cell.w
        }
    }
}

export function usePiiScannerAppV2() {
    const [file, setFile] = useState<File | null>(null)
    const [extension, setExtension] = useState<string>()
    const [fileContents, setFileContents] = useState<ArrayBuffer>()
    const [stage, setStage] = useState<0 | 1 | 2 | 3 | 4>(Stage.Loading)
    const [progress, setProgress] = useState(0)
    const [error, setError] = useState<string>()
    const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false)
    const [result, setResult] = useState<string>()

    const preloadModels = useMemo(
        () =>
            preloadAIAssets()
                .then(() => {
                    console.log("AI assets preloaded")
                })
                .catch((error) => {
                    console.error("Failed to preload AI assets", error)
                    if (global.window && window.navigator) {
                        console.log("UserAgent:", navigator.userAgent)
                    }
                }),
        []
    )

    useEffect(() => {
        try {
            const termsVersion = localStorage.getItem(acceptedTermsKey)

            if (termsVersion) {
                setAcceptedTerms(true)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setStage(Stage.Waiting)
        }
    }, [])

    useEffect(() => {
        try {
            if (stage === Stage.Done) {
                mixpanel.track("PII Scanner: Processed")
            } else if (stage === Stage.Processing) {
                mixpanel.track("PII Scanner: Processing")
            } else if (stage === Stage.Preparing) {
                mixpanel.track("PII Scanner: Preparing")
            }
        } catch (error) {
            console.error(error)
        }
    }, [stage])

    useEffect(() => {
        console.debug("Checking for new file")
        if (!file) return
        console.debug("New file detected", file.name)

        const fileName = file.name
        const extension = `.${(fileName.split(".").pop() ?? "").toLowerCase()}`

        if (acceptedExtensions.includes(extension)) {
            console.debug("Accepted file type", extension)

            // Valid
            setStage(Stage.Preparing)
            setExtension(extension)

            const reader = new FileReader()
            reader.readAsArrayBuffer(file)
            console.debug("Reading file")
            reader.onload = function (e) {
                console.debug("File loaded")
                if (e.target?.result) {
                    console.debug("File contents found")

                    const fileContents = e.target.result as ArrayBuffer
                    setFileContents(fileContents)

                    if (extension === AcceptFileTypeExtensions[AcceptFileType.TXT]) {
                        console.debug("Processing TXT file")

                        const fileContentsAsString = new TextDecoder().decode(fileContents)
                        const items = fileContentsAsString.split("\n")

                        console.debug("Processing %d items", items.length)

                        console.debug("Preload promise", preloadModels)

                        preloadModels.finally(() => {
                            console.debug("TXT processor has preloaded models")

                            processItemsInWorkerPool(items, 15, 10).then((results) => {
                                setResult(results.join("\n"))
                                setStage(Stage.Done)
                            })
                        })
                    } else if (extension === AcceptFileTypeExtensions[AcceptFileType.TSV]) {
                        console.debug("Processing TSV file")

                        const fileContentsAsString = new TextDecoder().decode(fileContents)
                        const lines = fileContentsAsString.split("\n")
                        const cells = lines.map((line) => line.split("\t")).flat()
                        const items = new Set<string>(cells)

                        preloadModels.finally(() => {
                            processItemsInWorkerPool(Array.from(items), 15, 10).then((results) => {
                                const mappedResults = new Map<string, string>()
                                const originals = Array.from(items)

                                for (let i = 0; i < originals.length; i++) {
                                    mappedResults.set(originals[i], results[i])
                                }

                                const processedLines: string[] = []
                                for (const line of lines) {
                                    const cells = line.split("\t")
                                    processedLines.push(
                                        cells
                                            .map((c) => {
                                                const item = mappedResults.get(c)
                                                return item ?? c
                                            })
                                            .join("\t")
                                    )
                                }

                                setResult(processedLines.join("\n"))
                                setStage(Stage.Done)
                            })
                        })
                    } else if (
                        [
                            AcceptFileTypeExtensions[AcceptFileType.CSV],
                            AcceptFileTypeExtensions[AcceptFileType.XLS],
                            AcceptFileTypeExtensions[AcceptFileType.XLSX],
                        ].includes(extension)
                    ) {
                        console.debug("Processing CSV/XLS/XLSX file")

                        const bookType =
                            extension === AcceptFileTypeExtensions[AcceptFileType.XLSX]
                                ? "xlsx"
                                : extension === AcceptFileTypeExtensions[AcceptFileType.XLS]
                                  ? "biff8"
                                  : "csv"
                        const workbook = read(fileContents, {
                            type: typeof fileContents === "string" ? "string" : "buffer",
                        })

                        const sheets = workbook.SheetNames
                        for (const sheet of sheets) {
                            const worksheet = workbook.Sheets[sheet]

                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const cells: Array<any> = []

                            for (const key in worksheet) {
                                if (key[0] === "!") continue
                                const cell = worksheet[key]
                                cells.push(cell)
                            }

                            // Collect all strings
                            const strings = new Set<string>()
                            cells.forEach((cell) => {
                                processCell(cell, (val) => {
                                    strings.add(val)
                                    return val
                                })
                            })

                            preloadModels.finally(() => {
                                processItemsInWorkerPool(Array.from(strings), 15, 10, (stats) => {
                                    setStage(Stage.Processing)
                                    setProgress(Math.ceil((stats.completedBatches / stats.totalBatches) * 100))
                                    console.log("Progress", stats)
                                }).then((results) => {
                                    const mappedResults = new Map<string, string>()
                                    const originals = Array.from(strings)

                                    for (let i = 0; i < originals.length; i++) {
                                        mappedResults.set(originals[i], results[i])
                                    }

                                    cells.forEach((cell) => {
                                        processCell(cell, (val) => {
                                            return mappedResults.get(val) ?? val
                                        })
                                    })

                                    const outBuf = write(workbook, {
                                        type: "buffer",
                                        bookType,
                                    })

                                    setResult(outBuf)
                                    setStage(Stage.Done)
                                })
                            })
                        }
                    } else {
                        console.error("Not yet supported", extension)
                    }
                } else {
                    console.error("No file contents")
                }
            }
        } else {
            console.debug("Invalid file type", extension)
            // Invalid
            mixpanel.track("PII Scanner: Error - Invalid file type")
            setError("Invalid file type, try again. We accept CSV, TSV, TXT, XLS and XLSX files.")
        }
    }, [file, preloadModels])

    const acceptTerms = useCallback(() => {
        setAcceptedTerms(true)
        if (global.window && localStorage) {
            localStorage.setItem(acceptedTermsKey, "1")
        }
    }, [])

    const reset = useCallback(() => {
        setFile(null)
        setStage(Stage.Waiting)
        setProgress(0)
        setError(undefined)

        if (global.window && window.sessionStorage !== undefined) {
            sessionStorage.removeItem(sessionKey)
        }
    }, [])

    const download = useCallback(() => {
        if (!result || !extension || !fileContents) throw new Error("No result or extension")
        const blob = new Blob([result], { type: convertExtensionToMimeType(extension) })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `redacted${extension}`
        a.click()
    }, [extension, fileContents, result])

    return {
        acceptTerms,
        acceptedTerms,
        download,
        error,
        stage,
        setFile,
        progress,
        result,
        reset,
    }
}
